import axios from "axios";
import { PaymentDetail } from "./components/types";
import { getValidToken } from "../../utils/authUtils";
import { Vehicle } from "../../services/api";

interface ApiResponse<T> {
  data: T;
  message?: string;
  isSuccess: boolean;
}

// API request/response types based on the documentation
export interface VehicleExpenseData {
  vehicleNumber: string;
  expenseDate: string;
  expenseType:
    | "FUEL"
    | "REPAIR"
    | "DRIVER_CLEANER"
    | "PARKING"
    | "OTHER"
    | "INSURANCE"
    | "CHALLAN"
    | "OUTSOURCED_OWNER"
    | "PICKUP_MAN"
    | "MINOR_REPAIR";
  amount: number;
  paidTo: string;
  location: string;
  contactNumber?: string;
  notes?: string;
  gst?: number;
}

export interface FuelExpenseDetail {
  fuelType: "Petrol" | "Diesel" | "CNG" | "Electric";
  liters: number;
  pricePerLitre: number;
  bulkDiscountApplied?: number;
  petrolPumpId?: string;
  odometerReading?: number;
}

export interface MaintenanceExpenseDetail {
  serviceType?: string;
  serviceProviderType?: "Garage" | "Mechanic";
  serviceProviderId?: string;
  maintenanceItem?: string;
  customMaintenanceItem?: string;
  actionType?:
    | "Repaired"
    | "Replaced"
    | "Serviced"
    | "Inspected"
    | "Adjusted"
    | "Cleaned";
  warrantyPeriod?: string;
  warrantyStartDate?: string;
  warrantyEndDate?: string;
  customWarrantyPeriod?: string;
  nextServiceDue?: number;
  invoiceNumber?: string;
  workOrderNumber?: string;
  odometerReading?: number;
}

export interface StaffParkingExpenseDetail {
  category:
    | "DRIVER"
    | "CLEANER"
    | "PARKING"
    | "TOLL"
    | "RTO"
    | "Challan"
    | "Food Allowance"
    | "Mobile Recharge"
    | "Loading/Unloading Fees"
    | "Night Halt Charges"
    | "Emergency Fund Used"
    | "Dhobi"
    | "Bus Cleaning (Interior)"
    | "Custom"
    | "OUTSOURCED_OWNER"
    | "PICKUP_MAN";
  referenceId?: string;
  customCategory?: string;
}

export interface ChallanExpenseDetail {
  responsibleParty: "DRIVER" | "CLEANER";
  responsiblePartyName: string;
  itemType: string;
  challanNumber: string;
  challanAction: string;
}

export interface CreateExpenseRequest {
  expenseData: VehicleExpenseData;
  detailData:
    | FuelExpenseDetail
    | MaintenanceExpenseDetail
    | StaffParkingExpenseDetail
    | ChallanExpenseDetail;
  paymentData?: Array<PaymentDetail>;
}

export interface VehicleExpenseResponse {
  id: string;
  vehicleNumber: string;
  vehicleId: string;
  expenseDate: string;
  expenseType: string;
  amount: number;
  paidTo: string;
  location: string;
  contactNumber?: string;
  notes?: string;
  gst?: number;
  createdAt: string;
  updatedAt: string;
  details?: any;
  payments?: PaymentDetail[];
  paidAmount?: number;
  balanceAmount?: number;
  vehicle?: Vehicle;
  masterReferenceId?: string;
  masterCategory?: string;
  companyId?: string;
  createdBy?: string;
}

// Extended interface for detailed expense response (used in edit mode)
export interface VehicleExpenseDetailResponse extends VehicleExpenseResponse {
  payments?: PaymentDetail[];
}

export interface ExpenseListResponse {
  expenses: VehicleExpenseResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ExpenseSummary {
  expenseType: string;
  count: string;
  totalAmount: string;
  averageAmount: string;
}

export interface FuelEfficiencyData {
  totalExpenses: number;
  totalLiters: string;
  totalAmount: string;
  averagePricePerLiter: string;
  expenses: VehicleExpenseResponse[];
}

export const vehicleExpenseApi = {
  fetchVehicles: async (filters?: {}): Promise<Vehicle[]> => {
    const token = await getValidToken();
    try {
      const url = `${
        process.env.REACT_APP_API_HOST
      }vehicles/v2/get?${new URLSearchParams(filters).toString()}`;
      const response = await axios.get<ApiResponse<Vehicle[]>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      return [];
    }
  },

  // Create a new expense with details
  createExpense: async (
    expenseRequest: CreateExpenseRequest
  ): Promise<VehicleExpenseResponse> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/v2`;
      const response = await axios.post<ApiResponse<VehicleExpenseResponse>>(
        url,
        expenseRequest,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to create expense");
    } catch (error) {
      console.error("Error creating expense:", error);
      throw error;
    }
  },

  // Update an existing expense
  updateExpense: async (
    id: string,
    expenseRequest: CreateExpenseRequest
  ): Promise<VehicleExpenseResponse> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/v2/${id}`;
      const response = await axios.put<ApiResponse<VehicleExpenseResponse>>(
        url,
        expenseRequest,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to update expense");
    } catch (error) {
      console.error("Error updating expense:", error);
      throw error;
    }
  },

  // Soft delete an existing expense
  createSoftDeleteExpense: async (
    expenseRequest: CreateExpenseRequest
  ): Promise<VehicleExpenseResponse> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/v2/soft-delete`;
      const response = await axios.put<ApiResponse<VehicleExpenseResponse>>(
        url,
        expenseRequest,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to update expense");
    } catch (error) {
      console.error("Error updating expense:", error);
      throw error;
    }
  },

  // Get a specific expense with details
  getExpense: async (id: string): Promise<VehicleExpenseDetailResponse> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/v2/${id}`;
      const response = await axios.get<
        ApiResponse<VehicleExpenseDetailResponse>
      >(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to fetch expense");
    } catch (error) {
      console.error("Error fetching expense:", error);
      throw error;
    }
  },

  // Get all expenses with filters
  getExpenses: async (params?: {
    page?: number;
    limit?: number;
    vehicleNumber?: string;
    expenseType?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: "ASC" | "DESC";
  }): Promise<any> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/v2`;
      const response = await axios.get<ApiResponse<VehicleExpenseResponse[]>>(
        url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        }
      );

      if (response.data?.isSuccess) {
        // Transform the response to match expected structure
        const expenses = response.data.data || [];
        return {
          expenses,
          pagination: {
            page: params?.page || 1,
            limit: params?.limit || 20,
            total: expenses.length,
            totalPages: Math.ceil(expenses.length / (params?.limit || 20)),
          },
        };
      }
      throw new Error(response.data?.message || "Failed to fetch expenses");
    } catch (error) {
      console.error("Error fetching expenses:", error);
      throw error;
    }
  },

  // Get recent expenses by type
  getRecentExpensesByType: async (
    expenseType: string,
    params?: {
      vehicleNumber?: string;
      limit?: number;
    }
  ): Promise<VehicleExpenseResponse[]> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/type/${expenseType}/recent`;
      const response = await axios.get<ApiResponse<VehicleExpenseResponse[]>>(
        url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        }
      );

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching recent expenses:", error);
      return [];
    }
  },

  // Get expenses by vehicle
  getExpensesByVehicle: async (
    vehicleNumber: string,
    params?: {
      page?: number;
      limit?: number;
    }
  ): Promise<ExpenseListResponse> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/vehicle/${vehicleNumber}`;
      const response = await axios.get<ApiResponse<ExpenseListResponse>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(
        response.data?.message || "Failed to fetch vehicle expenses"
      );
    } catch (error) {
      console.error("Error fetching vehicle expenses:", error);
      throw error;
    }
  },

  // Get expenses by date range
  getExpensesByDateRange: async (params: {
    startDate: string;
    endDate: string;
    vehicleNumber?: string;
    expenseType?: string;
  }): Promise<VehicleExpenseResponse[]> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/date-range`;
      const response = await axios.get<ApiResponse<VehicleExpenseResponse[]>>(
        url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        }
      );

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching expenses by date range:", error);
      return [];
    }
  },

  // Get expense summary for a vehicle
  getExpenseSummary: async (
    vehicleNumber: string,
    params: {
      startDate: string;
      endDate: string;
    }
  ): Promise<ExpenseSummary[]> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/vehicle/${vehicleNumber}/summary`;
      const response = await axios.get<ApiResponse<ExpenseSummary[]>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching expense summary:", error);
      return [];
    }
  },

  // Get fuel efficiency data
  getFuelEfficiency: async (
    vehicleNumber: string,
    params: {
      startDate: string;
      endDate: string;
    }
  ): Promise<FuelEfficiencyData | null> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/vehicle/${vehicleNumber}/fuel-efficiency`;
      const response = await axios.get<ApiResponse<FuelEfficiencyData>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching fuel efficiency:", error);
      return null;
    }
  },

  // Get warranties expiring soon
  getExpiringWarranties: async (
    days: number = 30
  ): Promise<VehicleExpenseResponse[]> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/warranties/expiring`;
      const response = await axios.get<ApiResponse<VehicleExpenseResponse[]>>(
        url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: { days },
        }
      );

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching expiring warranties:", error);
      return [];
    }
  },

  // Get expense statistics
  getExpenseStatistics: async (params: {
    startDate: string;
    endDate: string;
    vehicleNumber?: string;
  }): Promise<any> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/statistics`;
      const response = await axios.get<ApiResponse<any>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching expense statistics:", error);
      return null;
    }
  },

  // Bulk create expenses
  bulkCreateExpenses: async (
    expenses: CreateExpenseRequest[]
  ): Promise<VehicleExpenseResponse[]> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/v2/bulk`;
      const response = await axios.post<ApiResponse<VehicleExpenseResponse[]>>(
        url,
        { expenses },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to create expenses");
    } catch (error) {
      console.error("Error creating bulk expenses:", error);
      throw error;
    }
  },

  // Delete an expense
  deleteExpense: async (id: string): Promise<boolean> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/v2/${id}`;
      const response = await axios.delete<ApiResponse<any>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data?.isSuccess || false;
    } catch (error) {
      console.error("Error deleting expense:", error);
      return false;
    }
  },

  // Get expense by ID
  getExpenseById: async (id: string): Promise<any> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/v2/${id}`;
      const response = await axios.get<ApiResponse<any>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to fetch expense");
    } catch (error) {
      console.error("Error fetching expense by ID:", error);
      throw error;
    }
  },

  // Update payment
  createPayments: async (
    expenseId: string,
    paymentData: PaymentDetail[]
  ): Promise<any> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/v2/payments`;
      const response = await axios.post<ApiResponse<any>>(
        url,
        {
          expenseId,
          paymentData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to create payments");
    } catch (error) {
      console.error("Error creating payments:", error);
      throw error;
    }
  },

  // Delete payment
  deletePayments: async (paymentIds: string[]): Promise<boolean> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses/v2/payments/soft-delete`;
      const response = await axios.put<ApiResponse<any>>(url, paymentIds, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data?.isSuccess || false;
    } catch (error) {
      console.error("Error updating payments as deleted payments:", error);
      return false;
    }
  },

  // Legacy method for backward compatibility
  saveExpenses: async (data: any) => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicle-expenses`;
      const response = await axios.post(url, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error saving expenses:", error);
      throw error;
    }
  },

  getCurrentOdometerReadings: async (
    vehicleId: string,
    context: string
  ): Promise<any> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicles/v2/current-odometer-reading/${vehicleId}/${context}`;
      const response = await axios.get<ApiResponse<any>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data?.isSuccess) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error fetching current odometer readings:", error);
      return null;
    }
  },

  sendWhatsappMessage: async (data: any) => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicles/send-odometer-summary`;
      const response = await axios.post<ApiResponse<any>>(url, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data?.isSuccess) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error sending whatsapp message:", error);
      return null;
    }
  },

  getOutsourcedOwnerDetails: async (vehicleId: string): Promise<any> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicles/outsourced/owner/${vehicleId}`;
      const response = await axios.get<ApiResponse<any>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data?.isSuccess) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error fetching outsourced owner details:", error);
      return null;
    }
  },
};
